/**
 * DealerCardDisplay — dealer-branded "My Advisor" panel on /my-matches.
 *
 * Renders the advisor headshot on top (if uploaded), business name +
 * contact info under it, and the business card image below (if uploaded).
 * Silent if none of these have been provided by the dealer.
 */

import React from 'react';
import { UserCircle2 } from 'lucide-react';

const DealerCardDisplay = ({ dealerCard, isDark }) => {
  const hasCard = !!dealerCard?.business_card_url;
  const hasPhoto = !!dealerCard?.advisor_photo_url;
  if (!hasCard && !hasPhoto) return null;

  const businessName = dealerCard.business_name || 'Your RV advisor';

  return (
    <div
      className={`rounded-2xl ${isDark ? 'bg-gray-900/60 border border-gray-800' : 'bg-white border border-gray-200'} p-4 sm:p-5 shadow-sm`}
      data-testid="dealer-card-display"
    >
      <div className="flex items-center gap-2 mb-3">
        <UserCircle2 className="w-4 h-4 text-[#F2A900]" />
        <span className={`text-xs font-bold uppercase tracking-wider ${isDark ? 'text-gray-300' : 'text-gray-600'}`}>
          Your advisor at {businessName}
        </span>
      </div>

      {hasPhoto && (
        <div className="flex items-start gap-4 mb-4">
          <img
            src={dealerCard.advisor_photo_url}
            alt="Advisor headshot"
            className={`w-20 h-20 rounded-full object-cover ring-2 shrink-0 ${isDark ? 'ring-gray-700' : 'ring-gray-200'}`}
            loading="lazy"
            data-testid="dealer-advisor-photo"
          />
          <div className="min-w-0">
            <p className={`text-lg font-bold ${isDark ? 'text-white' : 'text-gray-900'}`}>{businessName}</p>
            {dealerCard.advisor_bio ? (
              <p
                className={`text-sm mt-1 whitespace-pre-line ${isDark ? 'text-gray-300' : 'text-gray-600'}`}
                data-testid="dealer-advisor-bio"
              >
                {dealerCard.advisor_bio}
              </p>
            ) : (
              <p className={`text-sm ${isDark ? 'text-gray-400' : 'text-gray-500'}`}>
                Here whenever you have questions.
              </p>
            )}
          </div>
        </div>
      )}

      {hasCard && (
        <div className={`rounded-xl overflow-hidden ring-1 ${isDark ? 'ring-gray-800 bg-gray-800' : 'ring-gray-200 bg-gray-50'}`}>
          <img
            src={dealerCard.business_card_url}
            alt={`${businessName} business card`}
            className="w-full max-w-md mx-auto block"
            loading="lazy"
            data-testid="dealer-business-card-image"
          />
        </div>
      )}
    </div>
  );
};

export default DealerCardDisplay;
